/**
 * billingApi.ts
 * Thin browser-side facade for all billing-related calls.
 * Keeps fetch details out of React components.
 */

type ApiResponse<T = unknown> = { success: boolean } & T

/*──────────────────────────── Checkout ────────────────────────────*/

/**
 * Starts Stripe Checkout for the chosen plan and redirects
 * the browser to the returned session URL.
 */
export async function startCheckout(planKey: "starter" | "pro", userId: string): Promise<void> {
  const res: ApiResponse<{ checkoutUrl: string }> = await fetch("/api/billing/create-checkout-session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      planKey,
      userId,
      returnUrl: window.location.href,
    }),
  }).then((r) => r.json())
  
  if (res.success) {
    const checkoutUrl = res.checkoutUrl.includes("checkout=mock")
      ? `${res.checkoutUrl}&type=subscription&plan=${planKey}`
      : res.checkoutUrl
    
      window.location.href = checkoutUrl
  } else {
    console.log("[v0] Checkout failed, no redirect")
  }
}

/**
 * Starts Stripe Checkout for a one-time credit pack purchase and redirects
 * the browser to the returned session URL.
 * @param packKey Application-defined credit pack key to purchase.
 * @param quantity Quantity multiplier for the selected credit pack.
 * @param userId Roomvia user id (UUID) for associating the checkout.
 * @param creditAmount The number of credits in this pack (for success messaging).
 */
export async function startCreditPackCheckout(
  packKey: string,
  quantity: number,
  userId: string,
  creditAmount: number,
): Promise<void> {
  const res: ApiResponse<{ checkoutUrl: string }> = await fetch("/api/billing/create-credit-pack-checkout-session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      packKey,
      quantity,
      userId,
      returnUrl: window.location.href,
    }),
  }).then((r) => r.json())
  if (res.success) {
    const checkoutUrl = res.checkoutUrl.includes("checkout=mock")
      ? `${res.checkoutUrl}&type=credits&pack=${packKey}&quantity=${quantity}&credits=${creditAmount}`
      : res.checkoutUrl
    
      window.location.href = checkoutUrl
  } else {
    console.log("[v0] Credit pack checkout failed, no redirect")
  }
}

/*──────────────────────────── Portal ──────────────────────────────*/

/**
 * Opens Stripe Billing Portal so the user can manage payment
 * methods, view invoices, or cancel their subscription.
 */
export async function openBillingPortal(userId: string): Promise<void> {
  const res: ApiResponse<{ portalUrl: string }> = await fetch("/api/billing/create-portal-session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      userId,
      returnUrl: window.location.href,
    }),
  }).then((r) => r.json())
  if (res.success) window.location.href = res.portalUrl
}

/*──────────────────────────── Admin ───────────────────────────────*/

/**
 * Forces the server to reload BillingPlan rows into memory.
 * Requires the X-Admin-Secret header (value in env RELOAD_PRICE_IDS).
 */
export async function refreshPlanCache(adminSecret: string): Promise<boolean> {
  const res: ApiResponse = await fetch("/api/billing/reload-plan-cache", {
    method: "POST",
    headers: { "x-admin-secret": adminSecret },
  }).then((r) => r.json())
  return res.success
}

/**
 * Lists configured billable actions with their units and active flag.
 * Requires the X-Admin-Secret header.
 */
export async function listBillableActions(
  adminSecret: string,
): Promise<ApiResponse<{ actions: Array<{ actionKey: string; units: number; isActive: boolean }> }>> {
  const res: ApiResponse<{ actions: Array<{ actionKey: string; units: number; isActive: boolean }> }> = await fetch(
    "/api/billing/admin/actions/list",
    {
      method: "GET"
    },
  ).then((r) => r.json())
  return res
}

/**
 * Sets units for a billable action (and optional metadata).
 * Requires the X-Admin-Secret header.
 * @param adminSecret Admin secret for authorization.
 * @param actionKey Billable action key to update.
 * @param units Non-negative integer units for the action.
 * @param opts Optional metadata (isActive, displayName, category, rolloutPolicy, effectiveFrom, updatedBy).
 */
export async function setBillableActionUnits(
  adminSecret: string,
  actionKey: string,
  units: number,
  opts?: {
    isActive?: boolean
    displayName?: string
    category?: string
    rolloutPolicy?: "immediate" | "start_of_month"
    effectiveFrom?: string | null
    updatedBy?: string
  },
): Promise<ApiResponse> {
  const res: ApiResponse = await fetch("/api/billing/admin/actions/set-units", {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-admin-secret": adminSecret },
    body: JSON.stringify({
      actionKey,
      units,
      ...(opts ?? {}),
    }),
  }).then((r) => r.json())
  return res
}

/*──────────────────────── Account & Billing (UI page helpers) ─────────────────────*/

/**
 * Returns the user's current primary subscription (if any).
 * @param userId Roomvia user id (UUID).
 */
export async function getCurrentSubscription(userId: string): Promise<ApiResponse<{ subscription: any | null }>> {
  const res: ApiResponse<{ subscription: any | null }> = await fetch(
    `/api/billing/subscriptions/current?userId=${encodeURIComponent(userId)}`,
    { method: "GET" },
  ).then((r) => r.json())
  return res
}

/**
 * Returns the user's upcoming invoice summary from Stripe, if available.
 * @param userId Roomvia user id (UUID).
 */
export async function getUpcomingInvoice(userId: string): Promise<
  ApiResponse<{
    invoice: { amount_due_cents: number; next_payment_attempt_at: string | null; period_end_at: string | null } | null
  }>
> {
  const res: ApiResponse<{
    invoice: { amount_due_cents: number; next_payment_attempt_at: string | null; period_end_at: string | null } | null
  }> = await fetch(`/api/billing/subscriptions/upcoming-invoice?userId=${encodeURIComponent(userId)}`, {
    method: "GET",
  }).then((r) => r.json())
  return res
}

/**
 * Changes the user's current plan to the specified planKey.
 * Server performs Stripe update (with prorations by default).
 * @param userId Roomvia user id (UUID).
 * @param planKey Target plan key, e.g., "starter" | "pro".
 */
export async function changePlan(
  userId: string,
  planKey: string,
): Promise<ApiResponse<{ subscriptionId: string; planKey: string }>> {
  const res: ApiResponse<{ subscriptionId: string; planKey: string }> = await fetch(
    "/api/billing/subscriptions/change-plan",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, planKey }),
    },
  ).then((r) => r.json())
  return res
}

/**
 * Cancels the user's subscription **immediately** and returns the number of plan units
 * granted to the Wallet via early_rollover along with the Stripe subscription id.
 * @param userId Roomvia user id (UUID).
 */
export async function cancelSubscription(
  userId: string,
): Promise<ApiResponse<{ canceled: boolean; subscriptionId: string; wallet_granted_units: number }>> {
  const res: ApiResponse<{ canceled: boolean; subscriptionId: string; wallet_granted_units: number }> = await fetch(
    "/api/billing/subscriptions/cancel",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    },
  ).then((r) => r.json())
  return res
}

/**
 * Reverses a pending "cancel at period end" on the user's subscription.
 * @param userId Roomvia user id (UUID).
 */
export async function uncancelSubscription(
  userId: string,
): Promise<ApiResponse<{ subscriptionId: string; at_period_end: boolean; status: string }>> {
  const res: ApiResponse<{ subscriptionId: string; at_period_end: boolean; status: string }> = await fetch(
    "/api/billing/subscriptions/uncancel",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    },
  ).then((r) => r.json())
  return res
}

/**
 * Lists the available credit pack catalog, enriched with Stripe price info (if live).
 * UI can request:
 *  - mode="nosub" (default public)
 *  - mode="sub"   (subscriber-only)
 *  - mode="both"  (show both prices)
 *  - mode="auto"  (requires userId; backend decides sub vs nosub)
 */
export async function listCreditPackCatalog(
  opts?: { mode?: "sub" | "nosub" | "both" | "auto"; userId?: string }
): Promise<
  ApiResponse<{
    catalog: Array<{
      packKey: string
      displayName: string
      creditUnits: number
      isActive: boolean
      stripePriceId: string
      stripePriceIdNosub: string
      amount_cents_sub?: number
      amount_cents_nosub?: number
    }>
  }>
> {
  const qs = new URLSearchParams()
  if (opts?.mode) qs.set("mode", opts.mode)
  if (opts?.userId) qs.set("userId", opts.userId)
  const url = qs.toString()
    ? `/api/billing/credit-packs/catalog?${qs.toString()}`
    : `/api/billing/credit-packs/catalog`

  const res = await fetch(url, { method: "GET" }).then((r) => r.json())
  return res
}

/**
 * Lists masked Stripe card payment methods for the user (no PANs stored).
 * @param userId Roomvia user id (UUID).
 */
export async function listPaymentMethods(userId: string): Promise<
  ApiResponse<{
    payment_methods: Array<{
      id: string
      brand: string
      last4: string
      exp_month: number
      exp_year: number
      is_default: boolean
    }>
  }>
> {
  const res: ApiResponse<{
    payment_methods: Array<{
      id: string
      brand: string
      last4: string
      exp_month: number
      exp_year: number
      is_default: boolean
    }>
  }> = await fetch(`/api/billing/payment-methods?userId=${encodeURIComponent(userId)}`, { method: "GET" }).then((r) =>
    r.json(),
  )
  return res
}

/**
 * Lists Stripe invoices for the user (paged).
 * @param userId Roomvia user id (UUID).
 * @param page 1-based page number (default 1).
 * @param pageSize Items per page (default 25).
 */
export async function listInvoices(
  userId: string,
  page?: number,
  pageSize?: number,
): Promise<
  ApiResponse<{
    page: number
    pageSize: number
    has_more: boolean
    items: Array<{
      id: string
      amount_due_cents: number
      amount_paid_cents: number
      status: string
      hosted_invoice_url: string | null
      created_at: string
    }>
  }>
> {
  const qs = new URLSearchParams({ userId })
  if (typeof page === "number") qs.set("page", String(page))
  if (typeof pageSize === "number") qs.set("pageSize", String(pageSize))
  const res: ApiResponse<{
    page: number
    pageSize: number
    has_more: boolean
    items: Array<{
      id: string
      amount_due_cents: number
      amount_paid_cents: number
      status: string
      hosted_invoice_url: string | null
      created_at: string
    }>
  }> = await fetch(`/api/billing/invoices?${qs.toString()}`, { method: "GET" }).then((r) => r.json())
  return res
}

/**
 * Returns optional marketing metadata for a plan (display name, features, etc.).
 * @param planKey Plan key to query (e.g., "starter" | "pro").
 */
export async function getPlanMarketing(planKey: string): Promise<ApiResponse<{ marketing: any | null }>> {
  const res: ApiResponse<{ marketing: any | null }> = await fetch(
    `/api/billing/subscriptions/plan-marketing?planKey=${encodeURIComponent(planKey)}`,
    { method: "GET" },
  ).then((r) => r.json())
  return res
}

/**
 * Returns the subscription plan catalog (no hardcoding). The payload is shaped
 * by the server and typically includes per-plan marketing copy and optional
 * Stripe amounts when live.
 */
export async function listSubscriptionPlanCatalog(): Promise<{
  plans: Array<{
    planKey: string
    monthlyQuota: number
    recurringPriceId: string
    usagePriceId: string
    marketing?: any
    recurring_amount_cents?: number
    usage_unit_amount_cents?: number
  }>
}> {
  const res: {
    plans: Array<{
      planKey: string
      monthlyQuota: number
      recurringPriceId: string
      usagePriceId: string
      marketing?: any
      recurring_amount_cents?: number
      usage_unit_amount_cents?: number
    }>
  } = await fetch("/api/billing/subscriptions/plan-catalog", { method: "GET" }).then((r) => r.json())
  res.plans = res.plans.filter((p) => p.planKey !== "meter_only")
  return res
}

/*──────────────────────── Existing read-route helpers ─────────────────────*/

/**
 * Returns the active subscriptions for a user.
 * @param userId Roomvia user id (UUID).
 */
export async function listActiveSubscriptions(userId: string): Promise<ApiResponse<{ subscriptions: any[] }>> {
  const res: ApiResponse<{ subscriptions: any[] }> = await fetch(
    `/api/billing/subscriptions/list-active?userId=${encodeURIComponent(userId)}`,
    { method: "GET" },
  ).then((r) => r.json())
  return res
}

/**
 * Returns the inactive subscriptions for a user.
 * @param userId Roomvia user id (UUID).
 */
export async function listInactiveSubscriptions(userId: string): Promise<ApiResponse<{ subscriptions: any[] }>> {
  const res: ApiResponse<{ subscriptions: any[] }> = await fetch(
    `/api/billing/subscriptions/list-inactive?userId=${encodeURIComponent(userId)}`,
    { method: "GET" },
  ).then((r) => r.json())
  return res
}

/**
 * Returns per-subscription DB usage balances for the current period.
 * @param userId Roomvia user id (UUID).
 * @param status Optional filter: "active" | "inactive" | "all" (defaults to active).
 */
export async function getSubscriptionBalances(
  userId: string,
  status?: "active" | "inactive" | "all",
): Promise<
  ApiResponse<{
    balances: Array<{ stripeSubscriptionId: string; planKey: string; planQuota: number; dbCommittedThisMonth: number }>
  }>
> {
  const qs = new URLSearchParams({ userId })
  if (status) qs.set("status", status)
  const res: ApiResponse<{
    balances: Array<{ stripeSubscriptionId: string; planKey: string; planQuota: number; dbCommittedThisMonth: number }>
  }> = await fetch(`/api/billing/subscriptions/balances?${qs.toString()}`, { method: "GET" }).then((r) => r.json())
  return res
}

/**
 * Returns paged credit pack purchases for a user.
 * @param userId Roomvia user id (UUID).
 * @param page 1-based page number (default 1).
 * @param pageSize Items per page (default 25).
 */
export async function getCreditPackPurchases(
  userId: string,
  page?: number,
  pageSize?: number,
): Promise<ApiResponse<{ page: number; pageSize: number; total: number; purchases: any[] }>> {
  const qs = new URLSearchParams({ userId })
  if (typeof page === "number") qs.set("page", String(page))
  if (typeof pageSize === "number") qs.set("pageSize", String(pageSize))
  const res: ApiResponse<{ page: number; pageSize: number; total: number; purchases: any[] }> = await fetch(
    `/api/billing/credit-packs/purchases?${qs.toString()}`,
    { method: "GET" },
  ).then((r) => r.json())
  return res
}

/**
 * Returns wallet units and Stripe monetary credit balance (cents) for a user,
 * plus an estimated credit units count derived from capacity.
 * @param userId Roomvia user id (UUID).
 */
export async function getCreditPackBalances(
  userId: string,
): Promise<ApiResponse<{ wallet_units: number; stripe_credit_cents: number; stripe_credit_units_estimate: number }>> {
  const res: ApiResponse<{ wallet_units: number; stripe_credit_cents: number; stripe_credit_units_estimate: number }> =
    await fetch(`/api/billing/credit-packs/balances?userId=${encodeURIComponent(userId)}`, { method: "GET" }).then(
      (r) => r.json(),
    )
  return res
}

/**
 * Returns capacity snapshot for the current period (plan remaining, wallet units, estimates).
 * @param userId Roomvia user id (UUID).
 */
export async function getUsageCapacity(userId: string): Promise<
  ApiResponse<{
    period_start: string
    plan_remaining_units: number
    wallet_units: number
    stripe_credit_units_estimate: number
    effective_capacity_units: number
  }>
> {
  const res: ApiResponse<{
    period_start: string
    plan_remaining_units: number
    wallet_units: number
    stripe_credit_units_estimate: number
    effective_capacity_units: number
  }> = await fetch(`/api/billing/usage/capacity?userId=${encodeURIComponent(userId)}`, { method: "GET" }).then((r) =>
    r.json(),
  )
  return res
}

/**
 * Returns an aggregated billing snapshot for dashboard use.
 * @param userId Roomvia user id (UUID).
 */
export async function getAggregatedBilling(userId: string): Promise<
  ApiResponse<{
    period_start: string
    subscriptions: any[]
    balances: Array<{ stripeSubscriptionId: string; planKey: string; planQuota: number; dbCommittedThisMonth: number }>
    credit_packs: { wallet_units: number; stripe_credit_cents: number; stripe_credit_units_estimate: number }
    capacity: {
      period_start: string
      plan_remaining_units: number
      wallet_units: number
      stripe_credit_units_estimate: number
      effective_capacity_units: number
    }
  }>
> {
  const res: ApiResponse<{
    period_start: string
    subscriptions: any[]
    balances: Array<{ stripeSubscriptionId: string; planKey: string; planQuota: number; dbCommittedThisMonth: number }>
    credit_packs: { wallet_units: number; stripe_credit_cents: number; stripe_credit_units_estimate: number }
    capacity: {
      period_start: string
      plan_remaining_units: number
      wallet_units: number
      stripe_credit_units_estimate: number
      effective_capacity_units: number
    }
  }> = await fetch(`/api/billing/aggregated?userId=${encodeURIComponent(userId)}`, { method: "GET" }).then((r) =>
    r.json(),
  )
  return res
}

/**
 * Returns unified transactions (Stripe invoices + credit-pack purchases) merged and sorted by date (desc).
 * @param userId Roomvia user id (UUID).
 * @param page Optional 1-based page number (default 1).
 * @param pageSize Optional page size (default 25).
 * @returns `{ success, page, pageSize, total, transactions }`
 */
export async function listTransactions(
  userId: string,
  page?: number,
  pageSize?: number,
): Promise<
  ApiResponse<{
    page: number
    pageSize: number
    total: number
    transactions: Array<{
      id: string
      kind: "invoice" | "credit_pack"
      amount_cents: number
      status: string
      created_at: string
      hosted_invoice_url?: string | null
      packKey?: string
      quantity?: number
      unitsTotal?: number
    }>
  }>
> {
  const qs = new URLSearchParams({ userId })
  if (typeof page === "number") qs.set("page", String(page))
  if (typeof pageSize === "number") qs.set("pageSize", String(pageSize))
  const res: ApiResponse<{
    page: number
    pageSize: number
    total: number
    transactions: Array<{
      id: string
      kind: "invoice" | "credit_pack"
      amount_cents: number
      status: string
      created_at: string
      hosted_invoice_url?: string | null
      packKey?: string
      quantity?: number
      unitsTotal?: number
    }>
  }> = await fetch(`/api/billing/transactions?${qs.toString()}`, { method: "GET" }).then((r) => r.json())
  return res
}

/**
 * Lists **credits used** (committed usage_ledger entries) for a user.
 * This wraps the route `GET /api/billing/usage/ledger` and supports
 * naive page-number pagination and an optional time window filter.
 *
 * @param userId   Roomvia user id (UUID).
 * @param page     Optional 1-based page number (defaults to 1).
 * @param pageSize Optional page size (defaults to 25).
 * @param fromIso  Optional ISO-8601 timestamp (inclusive) to filter `reported_at` from.
 * @param toIso    Optional ISO-8601 timestamp (exclusive) to filter `reported_at` to.
 * @returns        `{ success, page, pageSize, total, usages }`.
 */
export async function listUsageLedger(
  userId: string,
  page?: number,
  pageSize?: number,
  fromIso?: string,
  toIso?: string,
): Promise<
  ApiResponse<{
    page: number;
    pageSize: number;
    total: number;
    usages: Array<{
      usage_id: string;
      user_id: string;
      stripe_subscription_id: string | null;
      action_key: string;
      quantity: number;
      reported_at: string;
      created_at: string;
      session_id?: string | null;
    }>;
  }>
> {
  const qs = new URLSearchParams({ userId });
  if (typeof page === "number") qs.set("page", String(page));
  if (typeof pageSize === "number") qs.set("pageSize", String(pageSize));
  if (typeof fromIso === "string" && fromIso) qs.set("from", fromIso);
  if (typeof toIso === "string" && toIso) qs.set("to", toIso);

  const res: ApiResponse<{
    page: number;
    pageSize: number;
    total: number;
    usages: Array<{
      usage_id: string;
      user_id: string;
      stripe_subscription_id: string | null;
      action_key: string;
      quantity: number;
      reported_at: string;
      created_at: string;
      session_id?: string | null;
    }>;
  }> = await fetch(`/api/billing/usage/ledger?${qs.toString()}`, { method: "GET" }).then((r) => r.json());
  return res;
}

/**
 * Determines if an error message or error object indicates a billing-related issue.
 * Checks for error codes first (more reliable), then falls back to keyword matching.
 * Excludes network/upstream errors to prevent false positives.
 *
 * @param errorInput - Either an error message string or an error object with optional `code` and `message` fields
 * @returns true if the error is billing-related, false otherwise
 */
export function isBillingError(errorInput: string | { code?: string; message?: string }): boolean {
  // If it's an object with a code field, check for billing-specific error codes
  if (typeof errorInput === "object" && errorInput.code) {
    const billingErrorCodes = [
      "INSUFFICIENT_CREDITS",
      "QUOTA_EXCEEDED",
      "NO_ACTIVE_SUBSCRIPTION",
      "BILLING_REQUIRED",
      "PAYMENT_REQUIRED",
    ]
    if (billingErrorCodes.includes(errorInput.code)) {
      return true
    }
  }

  // Extract the message string
  const message = typeof errorInput === "string" ? errorInput : errorInput.message || ""
  const lowerMessage = message.toLowerCase()

  // Exclude network/upstream errors first (these are NOT billing errors)
  const networkErrorPatterns = [
    "network error",
    "upstream error",
    "connection error",
    "request failed with status code 5", // 500, 502, 503, 504
    "upstream connect error",
    "disconnect/reset before headers",
    "connection termination",
    "econnrefused",
    "enotfound",
    "etimedout",
  ]

  if (networkErrorPatterns.some((pattern) => lowerMessage.includes(pattern))) {
    return false
  }

  // Check for explicit billing error phrases (more specific than single keywords)
  const billingPhrases = [
    "insufficient credits",
    "not enough credits",
    "you don't have enough credits",
    "quota exceeded",
    "billing period",
    "upgrade your plan",
    "purchase additional credits",
    "no active subscription",
    "requires an active subscription",
    "subscription required",
  ]

  if (billingPhrases.some((phrase) => lowerMessage.includes(phrase))) {
    return true
  }

  // Only check for standalone "credits" or "quota" keywords if they appear
  // in a billing context (not in nested error data or headers)
  // This is a more conservative fallback
  const hasCreditsKeyword = /\b(credits?|quota)\b/i.test(message)
  const hasBillingContext = /\b(insufficient|not enough|purchase|upgrade|required)\b/i.test(message)

  return hasCreditsKeyword && hasBillingContext
}


/*──────────────────────────── End of file ────────────────────────────────*/
